import classNames from 'classnames';
import { memo, useMemo, useRef } from 'react';

import { useApiContext, useClickOutside, useGetUserCountry } from 'react-easyrocket';
import { useDropdown } from 'src/hooks/useDropdown';
import { arrayLanguagesOptions } from '../../config';
import { Arrow } from 'src/components/svg/Arrows';

import { useSetLanguageToProfile } from 'src/hooks/useSetLanguageToProfile';
import { useLanguageChange } from '../../hooks/useLanguageChange';
import { SupportedLanguages } from '../../types';
import { languageOptions } from '../../languageOptions';
import { useTranslation } from 'react-i18next';
import { getAffiliateData } from 'src/hooks/useGetCountryConfig/utils/getAffiliateData';

interface LanguagesProps {
  handleClick?: () => void;
  additionalContainerClassName?: string;
}

const Languages = ({ handleClick, additionalContainerClassName }: LanguagesProps) => {
  const {
    i18n: { language }
  } = useTranslation();
  const { toggle, showList, close } = useDropdown({});
  const { handleChangeLanguage } = useLanguageChange();
  const updateUserInfoForAuth = useSetLanguageToProfile();
  const { isGBR } = useGetUserCountry();
  const hasAffiliateLink = getAffiliateData();
  const { isAuthenticated } = useApiContext();

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const currentLanguage = languageOptions[language as SupportedLanguages];

  const handleOpenClick = () => {
    if (!showList) {
      handleClick?.();
    }
    toggle();
  };

  const handleSelectLanguage = (language: SupportedLanguages) => {
    handleChangeLanguage(language);
    updateUserInfoForAuth(language);
    toggle?.();
  };

  const languagesList = useMemo(() => {
    if (isGBR && (hasAffiliateLink || isAuthenticated)) {
      return arrayLanguagesOptions.filter((lang) => lang.labelCode !== 'en');
    }

    return arrayLanguagesOptions.filter((lang) => lang.labelCode !== 'gb');
  }, [isGBR, hasAffiliateLink, isAuthenticated]);

  useClickOutside(dropdownRef, close);

  return (
    <div
      className={classNames(
        'w-[190px] p-[1px] rounded-[20px] z-20 cursor-pointer',
        additionalContainerClassName
      )}>
      <div
        className="relative text-base  w-full z-20 border-[2px] border-b_secondary_border rounded-[20px] h-10"
        ref={dropdownRef}>
        <div
          className={classNames(
            'bg-b_bg_dropdown flex gap-2.5 relative flex-row items-center w-full h-full z-20 rounded-[20px] px-[18px]'
          )}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenClick();
          }}>
          {currentLanguage?.icon && (
            <currentLanguage.icon className="min-h-6 min-w-6 !max-w-6 !max-h-6 rounded-[20px]" />
          )}

          <p className="text-base text-b_border_dropdown font-bold not-italic">
            {currentLanguage?.labelName}
          </p>

          <Arrow
            fill="var(--b_border_dropdown)"
            className={classNames(
              'absolute right-5 w-3 h-full stroke-[3px] transition-transform stroke-b_border_dropdown',
              { opened: showList }
            )}
          />
        </div>
        {showList && (
          <div
            className={classNames(
              'languages absolute rounded-b-[20px] top-1/2 z-10 flex flex-col items-start overflow-y-scroll w-[calc(100%+2px)] left-[-1px] max-h-[150px] pt-6 pb-2 shadow bg-b_bg_dropdown border border-b_secondary_border'
            )}>
            {languagesList.map((option) => (
              <div
                className={classNames(
                  'pl-4 text-base text-b_text_dropdown font-bold not-italic cursor-pointer py-2 px-2 flex items-center gap-2.5 hover:text-b_border_dropdown transition-all',
                  {
                    '!text-b_border_dropdown': currentLanguage?.labelName === option.labelName
                  }
                )}
                onClick={() => handleSelectLanguage(option.labelCode)}
                key={option.labelName}>
                <>
                  <option.icon className="max-w-6 max-h-6 rounded-[20px]" />
                  {option.labelName}
                </>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(Languages);
